import { yupResolver } from "@hookform/resolvers/yup";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { ConstructionForm } from "~/components/common/constructions/ConstructionForm";
import {
	MANUAL_INPUT_CONSTRUCTION_ID,
	OTHER_CONSTRUCTION_ITEM_ID
} from "~/constants/DealConstructionConst";
import type { NskResponse } from "~/models/Api";
import type {
	DealConstruction,
	DealConstructionEdit
} from "~/models/DealConstruction";
import { validationSchemaConstruction } from "~/models/DealConstruction";
import type { DealConstructionItem } from "~/models/DealConstructionItem";
import { isSuccess } from "~/utils/api/api_handler";
import { ConstructionFormFooter } from "../ConstructionFormFooter";

type Props = {
	dealConstruction: DealConstruction;
	dealConstructionItems: DealConstructionItem[];
	disabled: boolean;
	handleClose: () => void;
	handleMutateConstructions: (() => Promise<void>) | null;
	handleUpdate: (
		submitValue: DealConstructionEdit,
		estimationId: number | null,
		historyId: number | null
	) => Promise<NskResponse>;
};

export const ConstructionEditModal: React.FC<Props> = ({
	dealConstruction,
	handleMutateConstructions,
	dealConstructionItems,
	handleClose,
	handleUpdate,
	disabled
}: Props) => {
	const defaultValues = React.useMemo((): DealConstructionEdit => {
		let dealConstructionItem: DealConstructionItem | null = null;

		// dealConstructionItemをセット
		// カスタム項目の場合はcustomItemableIdで取得
		// カスタム項目でない場合はconstructionItem.idで取得
		// 直接入力した場合は特殊なIDを持つdealConstructionItemとしてセット

		// カスタム項目の場合はcustomItemableIdで取得
		if (dealConstruction.customItemableId !== null) {
			dealConstructionItem =
				dealConstructionItems.find(
					(item) => item.id === dealConstruction.customItemableId
				) ?? null;
		}
		// 直接入力の場合（constructionItem.id = 9900 かつ otherName がある）
		else if (
			dealConstruction.constructionItem?.id === OTHER_CONSTRUCTION_ITEM_ID &&
			dealConstruction.otherName
		) {
			// 直接入力データを特殊なIDを持つdealConstructionItemとして保存
			dealConstructionItem = {
				constructionItemId: MANUAL_INPUT_CONSTRUCTION_ID,
				excludeEstimation: false,
				id: MANUAL_INPUT_CONSTRUCTION_ID,
				name: dealConstruction.otherName,
				overridable: false,
				seq: 0,
				unit: "",
				unitAmount: 0
			};
		}
		// その他だけどother_nameが空の場合
		// 現在は必須化しているが、過去データには空になるものが存在している
		else if (
			dealConstruction.constructionItem?.id === OTHER_CONSTRUCTION_ITEM_ID &&
			(dealConstruction.otherName === "" || dealConstruction.otherName === null)
		) {
			dealConstructionItem = null;
		}
		// 通常のマスタ項目の場合はconstructionItem.idで取得
		else if (dealConstruction.constructionItem?.id !== null) {
			dealConstructionItem =
				dealConstructionItems.find(
					(item) =>
						item.constructionItemId === dealConstruction.constructionItem?.id
				) ?? null;
		}

		return {
			...dealConstruction,
			dealConstructionItem,
			operationType:
				dealConstruction.type === "DealEstimationHistory"
					? dealConstruction.operationType
					: null,
			otherName: "" // 直接入力の場合はdealConstructionItem.nameに含めるためここは空にしてしまう
		};
	}, [dealConstruction, dealConstructionItems]);

	const methods = useForm<DealConstructionEdit>({
		defaultValues,
		mode: "onBlur",
		resolver: yupResolver(validationSchemaConstruction)
	});
	const executeUpdateDealConstruction =
		React.useCallback((): Promise<NskResponse> => {
			// 初回の見積りの場合は、dealEstimationを参照する
			if (dealConstruction.type === "DealEstimation") {
				methods.setValue("operationType", "edit");
				return handleUpdate(methods.getValues(), dealConstruction.id, null);
			}
			// operationTypeがaddの場合は、必ずDealEstimationHistoryが存在している
			if (dealConstruction.type === "DealEstimationHistory") {
				const { operationType, dealEstimation } = dealConstruction;
				if (operationType === "add") {
					return handleUpdate(methods.getValues(), null, dealConstruction.id);
				}
				if (dealEstimation !== null) {
					methods.setValue("operationType", "edit");
					return handleUpdate(methods.getValues(), dealEstimation.id, null);
				}
			}
			return handleUpdate(methods.getValues(), dealConstruction.id, null);
		}, [dealConstruction, handleUpdate, methods]);

	const onClickSubmit = React.useCallback(() => {
		methods.handleSubmit(async () => {
			const response = await executeUpdateDealConstruction();
			if (isSuccess(response)) {
				await handleMutateConstructions?.();
				methods.reset();
				handleClose();
				toast.success("見積りを更新しました");
				return;
			}
			toast.error("エラーが発生しました");
			handleClose();
			methods.reset();
		})();
	}, [
		executeUpdateDealConstruction,
		handleClose,
		handleMutateConstructions,
		methods
	]);

	return (
		<Modalv2 isOpen onClose={handleClose}>
			<ModalHeader title="見積りの修正" />
			<ModalBody>
				<FormProvider {...methods}>
					<ConstructionForm
						dealConstructionItems={dealConstructionItems}
						disabled={disabled}
					/>
					<ConstructionFormFooter
						disabled={disabled || methods.formState.isSubmitting}
						onClickSubmit={onClickSubmit}
						type="edit"
					/>
				</FormProvider>
			</ModalBody>
		</Modalv2>
	);
};
